/**
 * Supabase API layer for user daily activity tracking
 */

import { supabase } from '@/integrations/supabase/client';

export interface DailyActivity {
  [date: string]: number;
}

/** Fetch activity data for a user between two dates */
export async function fetchUserActivity(userId: string, sinceDate: string, untilDate?: string): Promise<DailyActivity> {
  let query = supabase
    .from('user_daily_activity' as any)
    .select('date, question_count')
    .eq('user_id', userId)
    .gte('date', sinceDate);

  if (untilDate) {
    query = query.lte('date', untilDate);
  }

  const { data, error } = await (query.order('date', { ascending: true })) as {
    data: { date: string; question_count: number }[] | null;
    error: any;
  };

  if (error) throw error;

  const activity: DailyActivity = {};
  for (const row of data ?? []) {
    activity[row.date] = row.question_count;
  }
  return activity;
}

/** Fetch distinct years that have activity data */
export async function fetchActivityYears(userId: string): Promise<number[]> {
  const { data, error } = await (supabase
    .from('user_daily_activity' as any)
    .select('date')
    .eq('user_id', userId)
    .order('date', { ascending: true })) as { data: { date: string }[] | null; error: any };

  if (error) throw error;

  const years = new Set<number>();
  for (const row of data ?? []) {
    years.add(new Date(row.date).getFullYear());
  }
  return Array.from(years).sort();
}

/** Increment today's question count by 1 via upsert */
export async function incrementDailyActivity(userId: string): Promise<void> {
  const today = new Date().toISOString().split('T')[0];

  // Try to get existing row
  const { data: existing } = await (supabase
    .from('user_daily_activity' as any)
    .select('id, question_count')
    .eq('user_id', userId)
    .eq('date', today)
    .maybeSingle()) as { data: { id: string; question_count: number } | null; error: any };

  if (existing) {
    await (supabase
      .from('user_daily_activity' as any)
      .update({ question_count: existing.question_count + 1, updated_at: new Date().toISOString() })
      .eq('id', existing.id)) as any;
  } else {
    await (supabase
      .from('user_daily_activity' as any)
      .insert({ user_id: userId, date: today, question_count: 1 })) as any;
  }
}
